import React, { useEffect, useState, createElement } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { Hero } from './components/Hero';
import { MarketingHero } from './components/MarketingHero';
import { ProductDemo } from './components/ProductDemo';
import { AICoworkers } from './components/AICoworkers';
import { StayInControl } from './components/StayInControl';
import { ToolComparison } from './components/ToolComparison';
import { HowItWorks } from './components/HowItWorks';
import { HowItWorksConnections } from './components/HowItWorksConnections';
import { AlwaysOn } from './components/AlwaysOn';
import { CapabilityGrid } from './components/CapabilityGrid';
import { QuickCapture } from './components/QuickCapture';
import { LivesInSlack } from './components/LivesInSlack';
import { ConnectedWorkflow } from './components/ConnectedWorkflow';
import { ComputerUse } from './components/ComputerUse';
import { Pricing } from './pages/Pricing';
import { WallOfLove } from './components/WallOfLove';
import { NarrativeSection } from './components/NarrativeSection';
import { MemorySection } from './components/MemorySection';
import { SecuritySection } from './components/SecuritySection';
import { PricingSection } from './components/PricingSection';
import { ClosingCTA } from './components/ClosingCTA';
import { Layout } from './components/Layout';
import { IntroSplash } from './components/IntroSplash';
import { Terms } from './pages/Terms';
import { Privacy } from './pages/Privacy';
import { Contact } from './pages/Contact';
import { Careers } from './pages/Careers';
import { Enterprise } from './pages/Enterprise';
import { ClientManagement } from './pages/ClientManagement';
import { GrowthMarketing } from './pages/GrowthMarketing';
import { FoundersExecs } from './pages/FoundersExecs';
import { Signup } from './pages/Signup';
import { Blog } from './pages/Blog';
import { Research } from './pages/Research';
import { CaseStudies } from './pages/CaseStudies';
import { Changelog } from './pages/Changelog';
import { Security } from './pages/Security';
import { CompareChatGPT } from './pages/CompareChatGPT';
import { WatchGigaDemo } from './pages/WatchGigaDemo';
import { BookDemo } from './pages/BookDemo';
import { Dashboard } from './pages/Dashboard';
import { Bespoke } from './pages/Bespoke';
import { Home } from './pages/Home';
import { HomeArchitecture } from './pages/HomeArchitecture';
import { HomeAlternative } from './pages/HomeAlternative';
import { EcommerceHome } from './pages/EcommerceHome';
import { FutureHome } from './pages/FutureHome';
import { Agents } from './pages/Agents';
import { WorkspaceHome } from './pages/WorkspaceHome';
import { SphereDirections } from './pages/SphereDirections';
import { ChatTransition } from './pages/ChatTransition';
import { ReferralCapture } from './pages/ReferralCapture';
import { UseCasesConcepts } from './pages/UseCasesConcepts';
import { FontOptions } from './pages/FontOptions';
import { ClaudeFont } from './pages/ClaudeFont';
import { Integrations } from './pages/Integrations';
import { Consulting } from './pages/Consulting';
import { CopyModeProvider } from './components/copy/copyMode';
function ScrollToTop() {
  const {
    pathname,
    hash
  } = useLocation();
  useEffect(() => {
    if (hash) {
      // Defer so the target route has mounted before we scroll to its anchor.
      const id = hash.replace('#', '');
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({
            behavior: 'smooth'
          });
          return;
        }
        window.scrollTo(0, 0);
      });
      return;
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}
function GoogleAnalytics() {
  useEffect(() => {
    // Inject the Google tag script
    const script1 = document.createElement('script');
    script1.async = true;
    script1.src = 'https://www.googletagmanager.com/gtag/js?id=G-2PE1STVTDF';
    document.head.appendChild(script1);
    // Inject the initialization script
    const script2 = document.createElement('script');
    script2.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'G-2PE1STVTDF');
    `;
    document.head.appendChild(script2);
    return () => {
      // Cleanup if needed (usually not necessary for analytics, but good practice)
      document.head.removeChild(script1);
      document.head.removeChild(script2);
    };
  }, []);
  return null;
}
/**
 * Sets <title> and <meta name="description"> per route.
 * Gets baked into each static HTML file by the prerenderer.
 */
function PageMeta() {
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    const titles: Record<string, string> = {
      '/': 'Giga — AI Workspace for Slack & Teams | Work Smarter',
      '/pricing': 'Pricing — Giga AI Workspace',
      '/security': 'Security — Giga | Enterprise-Grade AI Workspace',
      '/integrations': 'Integrations — Giga | Connect Your Tools to AI',
      '/enterprise': 'Enterprise AI Workspace — Giga for Organizations',
      '/blog': 'Blog — Giga | AI Workspace Tips & Insights',
      '/research': 'Research — Giga | AI Workspace Research',
      '/case-studies': 'Case Studies — Giga AI Workspace',
      '/changelog': 'Changelog — Giga | Product Updates',
      '/careers': 'Careers — Giga | Join Our Team',
      '/contact': 'Contact — Giga | Get in Touch',
      '/terms': 'Terms of Service — Giga',
      '/privacy': 'Privacy Policy — Giga',
      '/compare/chatgpt': 'Giga vs ChatGPT — AI Workspace Comparison',
      '/demo': 'Giga AI Workspace Demo — See It in Action',
      '/demo/book': 'Book a Demo — Giga AI Workspace',
      '/use-cases/founders-execs': 'AI Workspace for Founders & Execs — Giga',
      '/use-cases/client-management': 'AI for Client Management — Giga',
      '/use-cases/growth-marketing': 'AI for Growth Marketing — Giga',
      '/signup': 'Sign Up — Giga AI Workspace'
    };
    const descs: Record<string, string> = {
      '/': 'Giga is an AI workspace that connects your tools in Slack & Teams. Drop @Giga into any conversation and it pulls answers from all your apps.',
      '/pricing': 'See Giga pricing plans for teams and enterprises. Free trial available.',
      '/security': 'Enterprise-grade security for your AI workspace. Giga never exposes your credentials.',
      '/integrations': 'Connect Giga to Slack, Teams, Gmail, Notion, HubSpot, and more. One AI workspace for all your tools.',
      '/enterprise': 'Enterprise AI workspace with advanced security, SSO, and dedicated support for your organization.',
      '/blog': 'Read the latest insights on AI workspaces, productivity, and how teams use Giga.',
      '/compare/chatgpt': 'See how Giga compares to ChatGPT. Giga works across your tools, not just in a chat window.',
      '/demo': 'Watch the Giga AI workspace demo. See how it connects your tools and automates work in Slack.'
    };
    // Match exact path first, then prefix-based fallback for internal routes
    let title = titles[pathname];
    let desc = descs[pathname];
    if (!title) {
      // Internal review / experimental pages — keep concise
      for (const [prefix, t] of Object.entries(titles)) {
        if (pathname.startsWith(prefix) && prefix !== '/') {
          title = t;
          break;
        }
      }
    }
    document.title = title || 'Giga — AI Workspace for Slack & Teams';
    const meta = document.querySelector('meta[name="description"]');
    if (desc) {
      if (meta) {
        meta.setAttribute('content', desc);
      } else {
        const el = document.createElement('meta');
        el.name = 'description';
        el.content = desc;
        document.head.appendChild(el);
      }
    }
  }, [pathname]);
  return null;
}
export function App() {
  return <CopyModeProvider data-id="element-0">
      <GoogleAnalytics data-id="element-1" />
      <ScrollToTop data-id="element-2" />
      <PageMeta data-id="element-3" />
      {/* Global Prado-style film grain over the entire app */}
      <div className="grain-global" aria-hidden="true" data-id="element-4" />
      <Routes data-id="element-5">
        <Route path="/signup" element={<Signup data-id="element-7" />} data-id="element-6" />
        <Route path="/login" element={<Signup data-id="element-9" />} data-id="element-8" />
        {/* Waitlist referral capture: /r/<code> records the inbound referral
          and forwards into the waitlist flow with ?ref=<code>. */}
        <Route path="/r/:code" element={<ReferralCapture data-id="element-11" />} data-id="element-10" />
        <Route path="/dashboard" element={<Dashboard data-id="element-13" />} data-id="element-12" />
        {/* Internal review page: editorial directions for the sphere. */}
        <Route path="/sphere-directions" element={<SphereDirections data-id="element-15" />} data-id="element-14" />
        {/* Experimental: new card-morph hand-off from the integration sphere
          into the chat panel. Not linked from the marketing nav. */}
        <Route path="/chat-transition" element={<ChatTransition data-id="element-17" />} data-id="element-16" />
        {/* Internal review: 5 concepts for the use cases page */}
        <Route path="/use-cases-concepts" element={<UseCasesConcepts data-id="element-19" />} data-id="element-18" />
        {/* Internal review: headline font candidates shown in context */}
        <Route path="/font-options" element={<FontOptions data-id="element-21" />} data-id="element-20" />
        {/* Internal review: trying Claude's font on the home hero in context */}
        <Route path="/claude-font" element={<ClaudeFont data-id="element-23" />} data-id="element-22" />

        <Route path="/" element={<Layout data-id="element-25" />} data-id="element-24">
          {/* New home screen, built on the Tasklet-style hero. */}
          <Route index element={<Home data-id="element-27" />} data-id="element-26" />
          {/* Preview of the new architecture diagram section */}
          <Route path="architecture" element={<HomeArchitecture data-id="element-29" />} data-id="element-28" />
          {/* Alternative home screen with static hero and recaps feed */}
          <Route path="home-alt" element={<HomeAlternative data-id="element-31" />} data-id="element-30" />
          {/* E-commerce / DTC ICP-positioned home variant. */}
          <Route path="ecommerce" element={<EcommerceHome data-id="element-33" />} data-id="element-32" />
          {/* The previous Giga homepage, preserved at /future. */}
          <Route path="future" element={<FutureHome data-id="element-35" />} data-id="element-34" />
          {/* Task-focused agents showcase. */}
          <Route path="agents" element={<Agents data-id="element-37" />} data-id="element-36" />
          {/* Exploration: home page with an animated Slack-workspace hero. */}
          <Route path="workspace" element={<WorkspaceHome data-id="element-39" />} data-id="element-38" />
          <Route path="terms" element={<Terms data-id="element-41" />} data-id="element-40" />
          <Route path="privacy" element={<Privacy data-id="element-43" />} data-id="element-42" />
          <Route path="contact" element={<Contact data-id="element-45" />} data-id="element-44" />
          <Route path="careers" element={<Careers data-id="element-47" />} data-id="element-46" />

          <Route path="enterprise" element={<Enterprise data-id="element-49" />} data-id="element-48" />
          <Route path="bespoke" element={<Bespoke data-id="element-51" />} data-id="element-50" />
          <Route path="consulting" element={<Consulting data-id="element-53" />} data-id="element-52" />
          <Route path="pricing" element={<Pricing data-id="element-55" />} data-id="element-54" />
          <Route path="use-cases/founders-execs" element={<FoundersExecs data-id="element-57" />} data-id="element-56" />
          <Route path="use-cases/client-management" element={<ClientManagement data-id="element-59" />} data-id="element-58" />
          
          <Route path="use-cases/growth-marketing" element={<GrowthMarketing data-id="element-61" />} data-id="element-60" />
          
          <Route path="blog" element={<Blog data-id="element-63" />} data-id="element-62" />
          <Route path="research" element={<Research data-id="element-65" />} data-id="element-64" />
          <Route path="case-studies" element={<CaseStudies data-id="element-67" />} data-id="element-66" />
          <Route path="changelog" element={<Changelog data-id="element-69" />} data-id="element-68" />
          <Route path="security" element={<Security data-id="element-71" />} data-id="element-70" />
          <Route path="integrations" element={<Integrations data-id="element-73" />} data-id="element-72" />
          <Route path="compare/chatgpt" element={<CompareChatGPT data-id="element-75" />} data-id="element-74" />
          <Route path="demo" element={<WatchGigaDemo data-id="element-77" />} data-id="element-76" />
          <Route path="demo/book" element={<BookDemo data-id="element-79" />} data-id="element-78" />
        </Route>
      </Routes>
    </CopyModeProvider>;
}